import styled from "styled-components";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ChatSession, ChatMessage } from "../container/ChatHistoryContainer.hook";

interface SessionDetailModalProps {
  isOpen: boolean;
  session: ChatSession | null;
  messages: ChatMessage[];
  formatDate: (date: string) => string;
  onClose: () => void;
}

function SessionDetailModal({
  isOpen,
  session,
  messages,
  formatDate,
  onClose,
}: SessionDetailModalProps) {
  if (!isOpen || !session) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderContent>
            <Title>세션 내용 상세보기</Title>
            <SessionInfo>
              <InfoItem>
                <InfoLabel>세션 ID:</InfoLabel>
                <InfoValue>{session.id}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>사용자:</InfoLabel>
                <InfoValue>{session.userPhone}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>생성일:</InfoLabel>
                <InfoValue>{formatDate(session.createdAt)}</InfoValue>
              </InfoItem>
            </SessionInfo>
          </HeaderContent>
          <CloseButton onClick={onClose}>
            <XMarkIcon className="h-6 w-6" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <MessagesContainer>
            <MessagesTable>
              <TableHeader>
                <HeaderRow>
                  <HeaderCell style={{ width: "15%" }}>발화자</HeaderCell>
                  <HeaderCell style={{ width: "65%" }}>메시지 내용</HeaderCell>
                  <HeaderCell style={{ width: "20%" }}>전송 시각</HeaderCell>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {messages.map((message, index) => (
                  <TableRow key={index}>
                    <TableCell style={{ width: "15%" }}>
                      <RoleBadge $role={message.role}>
                        {message.role === "user" ? "사용자" : "어시스턴트"}
                      </RoleBadge>
                    </TableCell>
                    <TableCell style={{ width: "65%" }}>
                      <MessageContent>{message.content}</MessageContent>
                    </TableCell>
                    <TableCell style={{ width: "20%" }}>
                      <TimeStamp>{formatDate(message.createdAt)}</TimeStamp>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MessagesTable>
          </MessagesContainer>
        </ModalBody>

        <ModalFooter>
          <CloseButtonLarge onClick={onClose}>닫기</CloseButtonLarge>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
}

const Overlay = styled.div(() => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}));

const ModalContainer = styled.div(({ theme }) => ({
  backgroundColor: theme.color.white,
  borderRadius: 12,
  width: "90%",
  maxWidth: 1200,
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
}));

const ModalHeader = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: theme.spacing.large,
  borderBottom: `2px solid ${theme.color.lightGray}`,
}));

const HeaderContent = styled.div(() => ({
  flex: 1,
}));

const Title = styled.h2(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing.small,
  fontSize: 24,
  fontWeight: "bold",
  color: theme.color.black,
}));

const SessionInfo = styled.div(() => ({
  display: "flex",
  gap: 24,
  marginTop: 12,
}));

const InfoItem = styled.div(() => ({
  display: "flex",
  gap: 8,
  alignItems: "center",
}));

const InfoLabel = styled.span(({ theme }) => ({
  fontSize: 14,
  fontWeight: "600",
  color: theme.color.gray,
}));

const InfoValue = styled.span(({ theme }) => ({
  fontSize: 14,
  color: theme.color.black,
}));

const CloseButton = styled.button(({ theme }) => ({
  padding: 8,
  background: "transparent",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  color: theme.color.gray,
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: theme.color.lightGray,
    color: theme.color.black,
  },
}));

const ModalBody = styled.div(({ theme }) => ({
  flex: 1,
  overflow: "hidden",
  padding: theme.spacing.large,
  display: "flex",
  flexDirection: "column",
}));

const MessagesContainer = styled.div(() => ({
  width: "100%",
  height: "100%",
  overflow: "auto",
  position: "relative",
}));

const MessagesTable = styled.table(() => ({
  width: "100%",
  borderCollapse: "collapse",
}));

const TableHeader = styled.thead(({ theme }) => ({
  backgroundColor: theme.color.lightGray,
  position: "sticky",
  top: 0,
  zIndex: 10,
}));

const HeaderRow = styled.tr(() => ({}));

const HeaderCell = styled.th(({ theme }) => ({
  padding: "12px 16px",
  textAlign: "left",
  fontSize: 14,
  fontWeight: "700",
  color: theme.color.black,
  borderBottom: `2px solid ${theme.color.gray}`,
}));

const TableBody = styled.tbody(() => ({}));

const TableRow = styled.tr(({ theme }) => ({
  borderBottom: `1px solid ${theme.color.lightGray}`,
  "&:hover": {
    backgroundColor: "#f9fafb",
  },
}));

const TableCell = styled.td(({ theme }) => ({
  padding: "16px",
  fontSize: 14,
  color: theme.color.black,
  verticalAlign: "top",
}));

const RoleBadge = styled.span<{ $role: "user" | "assistant" }>(
  ({ theme, $role }) => ({
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: 12,
    fontSize: 13,
    fontWeight: "600",
    backgroundColor: $role === "user" ? "#dbeafe" : "#fce7f3",
    color: $role === "user" ? "#1e40af" : "#9f1239",
  })
);

const MessageContent = styled.div(() => ({
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));

const TimeStamp = styled.span(({ theme }) => ({
  fontSize: 13,
  color: theme.color.gray,
}));

const ModalFooter = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: theme.spacing.large,
  borderTop: `2px solid ${theme.color.lightGray}`,
}));

const CloseButtonLarge = styled.button(({ theme }) => ({
  padding: "10px 24px",
  fontSize: 14,
  fontWeight: "600",
  color: theme.color.white,
  backgroundColor: theme.color.gray,
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: theme.color.black,
  },
}));

export default SessionDetailModal;
